import { OverlayViewF, OverlayView } from '@react-google-maps/api'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { styles as clusterStyles } from 'components/Common/PropertyMap/Cluster'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import { HoveredOffer, HoveredPinType } from './SearchResultsMap'
import PositionedMapTile from 'components/Pages/HotelSearchPage/HotelMapMarker/PositionedMapTile'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarkerV2'
import { Position } from '../type'
import { getPositionAndOffsetOfTile } from './utils'

const Anchor = styled.div`
  transform: translate(-50%, -50%);
`

const Container = styled.div`
  position: relative;
  display: grid;
`

const TextContainer = styled.div`
  position: absolute;
  place-self: center;
`

const MarkerContainer = styled.div`
  filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.4));
  position: relative;
  top: ${rem(-8)};
`

interface Props {
  onClusterClick: (markers: Array<App.SearchMapMarker>) => void;
  count: number;
  latitude: number;
  longitude: number;
  markers: Array<App.SearchMapMarker>;
  hoveredOfferId?: string;
  filters: App.OfferListFilters;
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number,
  ) => void;
  onDeselectOffers: () => void;
  mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  onHover?: (offerId: string, type: string) => void
}

function ClusterMarker(props: Props) {
  const {
    count,
    latitude,
    longitude,
    onClusterClick,
    markers,
    hoveredOfferId,
    filters,
    onDeselectOffers,
    handleProductClick,
    mapContainerRef,
    onHover,
  } = props

  const [clusteredOfferIds, setClusteredOfferIds] = useState<Set<string>>(
    new Set(),
  )
  const [isHovered, setIsHovered] = useState(false)
  const [isPinHovered, setIsPinHovered] = useState(false)
  const [localHoveredOffer, setLocalHoveredOffer] = useState<
    HoveredOffer | undefined
  >(undefined)
  const [isPinActive, setIsPinActive] = useState(false)
  const [tilePosition, setTilePosition] = useState<Position | undefined>(undefined)
  const [tileOffset, setTileOffset] = useState<{ xOffset: number; yOffset: number } | undefined>(undefined)

  useEffect(() => {
    const offerIds = new Set(markers.map(({ id }) => id))
    setClusteredOfferIds(offerIds)
  }, [markers])

  useEffect(() => {
    if (hoveredOfferId && clusteredOfferIds.has(hoveredOfferId)) {
      const hoveredOffer = markers.find(({ id }) => id === hoveredOfferId)
      if (hoveredOffer?.id !== localHoveredOffer?.id) {
        setLocalHoveredOffer({
          id: hoveredOffer?.id ?? '',
          type: hoveredOffer?.offers[0].type ?? '',
        })
      }
    } else if (hoveredOfferId && hoveredOfferId !== '') {
      setLocalHoveredOffer(undefined)
      setIsPinActive(false)
      setIsPinHovered(false)
    }
  }, [clusteredOfferIds, hoveredOfferId, isHovered, localHoveredOffer?.id, markers])

  const handleClick = useCallback(() => {
    onClusterClick(markers)
  }, [markers, onClusterClick])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handlePinClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      setIsPinActive(true)
      const { position, offset } = getPositionAndOffsetOfTile(
        e,
        mapContainerRef,
        localHoveredOffer?.type === HoveredPinType.BEDBANK,
      )
      setTilePosition(position)
      setTileOffset(offset)
      e.stopPropagation()
    },
    [localHoveredOffer?.type, mapContainerRef],
  )

  const handleDeselectOffers = useCallback(() => {
    setIsPinActive(false)
    setIsPinHovered(false)
    onDeselectOffers()
  }, [onDeselectOffers])

  const handlePinMouseEnter = useCallback(() => {
    if (onHover && localHoveredOffer?.id) {
      if (localHoveredOffer?.id !== hoveredOfferId) {
        onHover(localHoveredOffer?.id, localHoveredOffer?.type)
      }
    }
    setIsPinHovered(true)
  }, [hoveredOfferId, localHoveredOffer?.id, localHoveredOffer?.type, onHover])

  const handlePinMouseLeave = useCallback(() => {
    if (onHover) {
      onHover('', '')
    }
    setIsPinHovered(false)
  }, [onHover])

  const marker: App.SearchMapMarker = useMemo(() => ({
    id: localHoveredOffer?.id ?? '',
    offers: [
      {
        offerId: localHoveredOffer?.id ?? '',
        propertyId: '',
        type: localHoveredOffer?.type ?? '',
        kind: '',
        listPosition: -1,
        pricing: {},
      },
    ],
    latitude,
    longitude,
  }), [latitude, localHoveredOffer?.id, localHoveredOffer?.type, longitude])

  return (
    <>
      <OverlayViewF
        key={`${latitude}-${longitude}`}
        position={{
          lat: latitude,
          lng: longitude,
        }}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        zIndex={isHovered ? 20 : 0}
      >
        <Anchor>
          <Clickable onClick={handleClick}>
            <Container
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <img
                src={clusterStyles.black.url}
                height={clusterStyles.black.height}
                width={clusterStyles.black.width}
                alt=""
              />
              <TextContainer>
                <BodyText
                  variant="medium"
                  colour="neutral-eight"
                >
                  {count}
                </BodyText>
              </TextContainer>
            </Container>
          </Clickable>
        </Anchor>
      </OverlayViewF>
      {localHoveredOffer && (
        <OverlayViewF
          key={localHoveredOffer?.id}
          position={{
            lat: latitude,
            lng: longitude,
          }}
          zIndex={21}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <Clickable onClick={handlePinClick} onMouseEnter={handlePinMouseEnter} onMouseLeave={handlePinMouseLeave}>
            <MarkerContainer>
              <PropertyMarker
                key={localHoveredOffer.id}
                marker={marker}
                active={isPinActive}
                isHovered={isPinHovered}
                visited={false}
                soldOut={false}
              />
            </MarkerContainer>
          </Clickable>
          {isPinActive &&
            <PositionedMapTile
              offerIds={[localHoveredOffer.id]}
              filters={filters}
              id={localHoveredOffer.id}
              tilePosition={tilePosition}
              tileOffset={tileOffset}
              onProductClick={handleProductClick}
              onDeselectOffers={handleDeselectOffers}
            />
          }
        </OverlayViewF>
      )}
    </>
  )
}

export default ClusterMarker
